import type { Schema } from '@tix/openapi-sdk'

export const DONATION_SURVEY_KEYS = {
  anonymous: 'donate_anonymous',
  dedicate: 'donate_dedicate',
  honoreeFirstName: 'donate_honoree_first',
  honoreeLastName: 'donate_honoree_last',
} as const

export type DonationOptionsValues = {
  anonymous: boolean
  dedicate: boolean
  honoreeFirstName: string
  honoreeLastName: string
}

export function emptyDonationOptionsValues(): DonationOptionsValues {
  return {
    anonymous: false,
    dedicate: false,
    honoreeFirstName: '',
    honoreeLastName: '',
  }
}

export type DonationSurveySpec = NonNullable<Schema<'ticket_group'>['additional_info_spec']>

export type DonationOptionsVisibility = 'dedicate' | 'anonymous' | 'both' | null

/** One ticket group's survey answers (`reserve.additional_info[].data`). */
export type DonationSurveyData = NonNullable<
  NonNullable<Schema<'reserve_tickets_request'>['additional_info']>[number]['data']
>

function specHasKey(spec: DonationSurveySpec | null | undefined, key: string): boolean {
  return key in (spec?.properties ?? {})
}

function hasDedicationKeys(spec: DonationSurveySpec | null | undefined): boolean {
  return (
    specHasKey(spec, DONATION_SURVEY_KEYS.dedicate) &&
    specHasKey(spec, DONATION_SURVEY_KEYS.honoreeFirstName) &&
    specHasKey(spec, DONATION_SURVEY_KEYS.honoreeLastName)
  )
}

function hasAnonymityKey(spec: DonationSurveySpec | null | undefined): boolean {
  return specHasKey(spec, DONATION_SURVEY_KEYS.anonymous)
}

/** Whether the donation options UI should render, and which sections to show. */
export function getDonationOptionsVisibility(spec: DonationSurveySpec | null | undefined): DonationOptionsVisibility {
  const hasDedication = hasDedicationKeys(spec)
  const hasAnonymity = hasAnonymityKey(spec)

  if (hasDedication && hasAnonymity) return 'both'
  if (hasDedication) return 'dedicate'
  if (hasAnonymity) return 'anonymous'
  return null
}

export function buildDonationSurveyData(
  options: DonationOptionsValues,
  spec: DonationSurveySpec | null | undefined,
): DonationSurveyData | null {
  const data: DonationSurveyData = {}

  if (specHasKey(spec, DONATION_SURVEY_KEYS.anonymous)) {
    data[DONATION_SURVEY_KEYS.anonymous] = options.anonymous
  }

  if (specHasKey(spec, DONATION_SURVEY_KEYS.dedicate)) {
    data[DONATION_SURVEY_KEYS.dedicate] = options.dedicate
  }

  // Honoree names are omitted rather than blanked when there's no dedication, which is what
  // clears them from a cart that was synced while the dedication was still ticked.
  if (options.dedicate) {
    if (specHasKey(spec, DONATION_SURVEY_KEYS.honoreeFirstName)) {
      data[DONATION_SURVEY_KEYS.honoreeFirstName] = options.honoreeFirstName.trim()
    }
    if (specHasKey(spec, DONATION_SURVEY_KEYS.honoreeLastName)) {
      data[DONATION_SURVEY_KEYS.honoreeLastName] = options.honoreeLastName.trim()
    }
  }

  return Object.keys(data).length > 0 ? data : null
}

function cartSurveyData(
  cartAdditionalInfo: Schema<'cart'>['additional_info'],
  ticketGroupId: string,
): DonationSurveyData {
  const data = cartAdditionalInfo?.[ticketGroupId]
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return {}
  }

  return data as DonationSurveyData
}

/**
 * The inverse of {@link buildDonationSurveyData}: reads a cart's stored survey answers back into form values. Used after a redirect payment returns, when the page that collected the answers is gone and the cart is the only record of them.
 */
export function donationOptionsFromCart(
  cartAdditionalInfo: Schema<'cart'>['additional_info'],
  ticketGroupId: string,
): DonationOptionsValues {
  const data = cartSurveyData(cartAdditionalInfo, ticketGroupId)

  return {
    anonymous: data[DONATION_SURVEY_KEYS.anonymous] === true,
    dedicate: data[DONATION_SURVEY_KEYS.dedicate] === true,
    honoreeFirstName: String(data[DONATION_SURVEY_KEYS.honoreeFirstName] ?? ''),
    honoreeLastName: String(data[DONATION_SURVEY_KEYS.honoreeLastName] ?? ''),
  }
}

function isShallowEqual(a: DonationSurveyData, b: DonationSurveyData): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)])
  for (const key of keys) {
    if (a[key] !== b[key]) {
      return false
    }
  }

  return true
}

/**
 * The survey answers to send as `reserve.additional_info` for a ticket group, or `null` when
 * there is nothing to sync — either the group collects none of the canonical keys, or the cart
 * already holds these answers.
 */
export function buildDonationSurveyUpdate(
  options: DonationOptionsValues,
  spec: DonationSurveySpec | null | undefined,
  cartAdditionalInfo: Schema<'cart'>['additional_info'],
  ticketGroupId: string,
): DonationSurveyData | null {
  const answers = buildDonationSurveyData(options, spec)
  if (!answers) {
    return null
  }

  if (isShallowEqual(answers, cartSurveyData(cartAdditionalInfo, ticketGroupId))) {
    return null
  }

  return answers
}
